import PropTypes from 'prop-types'
import React from 'react'

import {
  createAtHomeSlot,
  fetchAtHomeApartments,
  fetchAtHomeCategories,
  fetchAtHomeSlots,
  fetchAtHomeTeachers,
  updateAtHomeSlot,
  withdrawAtHomeSlot
} from 'api/atHome'
import Button from 'app/components/generic/Button'

import BookTrialDialog from './BookTrialDialog'
import { GRADES, slotRow, slotHasStarted } from './format'
import AtHomeCrudPage from './shared/AtHomeCrudPage'

/**
 * "Listing the trial sessions": every open slot the admin has uploaded, with
 * the teacher already attached. New slot / Edit / Delete (with confirmation), and Book per row.
 */
// What became of a slot whose date has passed.
const outcome = (s) => {
  if (!s.booking) return 'Unsold'
  switch (s.booking.status) {
    case 'completed':
      return 'Attended'
    case 'no_show':
      return 'No-show'
    case 'cancelled':
      return 'Booking cancelled'
    default:
      return 'Booked, not closed by teacher'
  }
}

// Open, but the teacher is booked for another session at that time (a module
// paid over a trial slot). Whichever is paid first wins (owner, 2026-11-03),
// so it cannot be booked; the server refuses it too.
const teacherBusy = (s) => (s.teacherBusyWith || []).length > 0

// mode 'schedule' = the scheduling team's view: list, add, edit, delete.
// mode 'book' = the booking team's view: upcoming open slots with Book only
// (management, 2026-10-09: scheduling and booking are different people).
const AtHomeSlots = ({ kind = 'trial', mode = 'schedule' }) => {
  const [categories, setCategories] = React.useState([])
  const [apartments, setApartments] = React.useState([])
  const [teachers, setTeachers] = React.useState([])

  /**
   * Keep a teacher in the option list even when the latest search did not
   * return them.
   *
   * Two cases need it. EDITING a slot: the row's own teacher must be among the
   * options or MUI renders the box empty (the value is still correct and still
   * saves, but an empty box on an edit form is how someone picks the wrong
   * person). And AFTER a search: once the admin has chosen someone, typing
   * again replaces the list, and the chosen row must not vanish from under the
   * selection.
   */
  const rememberTeacher = React.useCallback((t) => {
    if (!t || !t.uuid) return
    setTeachers((prev) =>
      prev.some((x) => x.uuid === t.uuid) ? prev : [t, ...prev]
    )
  }, [])
  const [booking, setBooking] = React.useState(null)
  const [refreshKey, setRefreshKey] = React.useState(0)

  React.useEffect(() => {
    fetchAtHomeCategories()
      .then(setCategories)
      .catch(() => {})
    fetchAtHomeApartments()
      .then(setApartments)
      .catch(() => {})
    fetchAtHomeTeachers()
      .then(setTeachers)
      .catch(() => {})
  }, [])

  // Every slot of this kind, past and future (owner, 2026-10-03): one place
  // for a slot's whole life. Past rows are read-only; upcoming sort first.
  // Deleted slots stay out — they were never on offer.
  // `range` is the header's date window, applied SERVER-side (`from`/`to`):
  // this is the list that ships 707 rows to show 25, and `status: 'all'`
  // above is what makes it that long. Narrowing the window is the only one of
  // the two that the grid's own column filters cannot do for themselves.
  const fetchList = React.useCallback(
    async (range) => {
      const all = await fetchAtHomeSlots({ status: 'all', kind }, range)
      const rows = all
        .filter((s) => s.status !== 'cancelled')
        .map((s) => {
          const past = slotHasStarted(s.date, s.start_time)
          return {
            ...slotRow(s),
            past,
            whenLabel: past ? 'Past' : 'Upcoming',
            outcomeLabel: past
              ? outcome(s)
              : s.status === 'booked'
              ? 'Booked'
              : teacherBusy(s)
              ? 'Teacher busy'
              : 'Open',
            childName: s.booking?.child?.name || ''
          }
        })
      const key = (r) => `${r.date} ${r.start_time}`
      if (mode === 'book')
        return rows
          .filter((r) => !r.past && r.status === 'open')
          .sort((a, b) => key(a).localeCompare(key(b)))
      return [
        ...rows
          .filter((r) => !r.past)
          .sort((a, b) => key(a).localeCompare(key(b))),
        ...rows
          .filter((r) => r.past)
          .sort((a, b) => key(b).localeCompare(key(a)))
      ]
    },
    [kind, mode]
  )

  const fields = [
    {
      name: '_who',
      type: 'note',
      label:
        'A parent only sees this session if it is at their own building, on a day still to come, and open to their child’s grade.'
    },
    {
      name: 'category',
      label: 'Subject — parents see this name',
      type: 'select',
      options: categories
        .filter((c) => c.status !== 'inactive')
        .map((c) => ({ value: c.uuid, label: c.name }))
    },
    {
      name: 'teacher',
      label: 'Teacher who comes to the home',
      type: 'searchselect',
      /**
       * SEARCHED ON THE SERVER. `admin/teachers/` returns the first 200 by
       * first name and filters on name OR phone when given `q` -- and there
       * are 1,250 teachers, so loading it once and filtering in the browser
       * made everyone from "Bi" onwards unreachable, with no request fired to
       * say so. The endpoint always had the search; the form was not asking.
       */
      onSearch: (text) =>
        fetchAtHomeTeachers(text)
          .then(setTeachers)
          .catch(() => {}),
      options: teachers.map((t) => ({
        value: t.uuid,
        label: t.phone ? `${t.name} · ${t.phone}` : t.name
      }))
    },
    {
      name: 'apartment',
      label: 'Building — only parents here see it',
      type: 'searchselect',
      options: apartments
        .filter((a) => a.active)
        .map((a) => ({
          value: a.uuid,
          label: a.area ? `${a.name} (${a.area})` : a.name
        }))
    },
    { name: 'date', label: 'Day the teacher comes', type: 'date' },
    { name: 'start_time', label: 'Start time', type: 'time' },
    {
      name: 'duration_minutes',
      label: 'How long the class runs (minutes)',
      type: 'number'
    },
    {
      // MULTI-SELECT (owner, 2026-10-20). One session can be listed for
      // several classes, which is what `AtHomeSlot.grades` now stores -- the
      // same shape, and the same `GRADES` options, that projects already use.
      //
      // This replaces a single-choice box plus a note apologising that "Any"
      // could not be picked in it: `GRADES` is classes only, so every
      // open-to-everyone session on prod had to be made by script. Ticking all
      // eight classes now says the same thing, so the wildcard is not needed
      // and the note is gone with it.
      //
      // EMPTY = EVERY CLASS, which is what a session with no class has always
      // meant. It is the opposite of the projects field above, where empty
      // means nothing matches -- the help text says so rather than leaving it
      // to be discovered.
      name: 'grades',
      label: 'Classes this session is for',
      type: 'multiselect',
      options: GRADES,
      help: 'Tick every class that may book it. Leave empty (or tick all eight) to open it to every class we run.'
    },
    { name: 'price', label: 'What the parent pays (₹)', type: 'number' },
    // REMOVED 2026-10-19 (owner): the per-slot 'What is being made this hour',
    // 'Session photo' and 'What the child will do'. What the parent reads is
    // the subject's trial wording from Setup → Categories; no slot on prod
    // ever carried its own. The API no longer accepts the three keys.
    {
      name: 'notes',
      label: 'Note for the teacher — shown on their session screen',
      type: 'textarea'
    }
  ]

  return (
    <>
      <AtHomeCrudPage
        subtitle={
          mode === 'book'
            ? 'Open trial slots. The first payment books a slot.'
            : 'Trial slots listed for parents to book.'
        }
        entityLabel="Session"
        fetchList={fetchList}
        dateRange
        createItem={mode === 'schedule' ? createAtHomeSlot : undefined}
        updateItem={mode === 'schedule' ? updateAtHomeSlot : undefined}
        deleteItem={mode === 'schedule' ? withdrawAtHomeSlot : undefined}
        columns={[
          // Same order as Schedules → Regular (owner, 2026-10-21): type,
          // category, what it is, teacher, when, where, who for, then the rest.
          { name: 'kindLabel', label: 'Session Type' },
          { name: 'categoryName', label: 'Category' },
          ...(kind === 'trial'
            ? [{ name: 'trialName', label: 'Session' }]
            : []),
          { name: 'teacherName', label: 'Teacher' },
          { name: 'dateLabel', label: 'Date', options: { keepOrder: true } },
          // The time range already shows the length, so no Duration column.
          { name: 'timeLabel', label: 'Time', options: { keepOrder: true } },
          { name: 'whenLabel', label: 'Timeline' },
          { name: 'apartmentName', label: 'Apartment' },
          // `gradesLabel`, NOT `grade`: the latter is the legacy one-field
          // summary and renders the bare word "Any", which names no classes
          // (owner, 2026-10-21). `gradesLabel` lists what is actually ticked
          // -- "UKG,1,2,3,4,5,6,7,8". This column was moved here from
          // SESSION_COLUMNS when the tabs were given a shared order, and the
          // raw field came with it.
          { name: 'gradesLabel', label: 'Grades' },
          { name: 'priceLabel', label: 'Price' },
          // What the parent is booked onto, as the parent sees it. `trialName`
          // is the API's `display_name`: the subject's trial wording from
          // Setup → Categories. The per-slot description and photo columns
          // went with the fields behind them (owner, 2026-10-19).
          ...(mode === 'book'
            ? [{ name: 'linksOut', label: 'Payment links' }]
            : []),
          { name: 'outcomeLabel', label: 'Status' },
          { name: 'childName', label: 'Child' },
          { name: 'notes', label: 'Teacher note', options: { wrap: true } }
        ]}
        canChange={(row) => !row.past && row.status === 'open'}
        fields={fields}
        emptyValues={{
          duration_minutes: 60,
          // A NEW SESSION IS OPEN TO EVERYONE by default, which is how every
          // one on production is listed. Narrowing is the deliberate act.
          grades: GRADES,
          kind,
          price: ''
        }}
        rowToForm={(row) => {
          // The row's teacher may not be in the first 200; without this the
          // edit dialog opens with the teacher box blank.
          rememberTeacher({ uuid: row.teacher, name: row.teacherName })
          return {
            ...row,
            start_time: (row.start_time || '').slice(0, 5),
            // An un-migrated row has no `grades` yet. Read its old single value
            // rather than opening the box empty and letting a save narrow a slot
            // nobody meant to touch -- the exact trap the removed note warned
            // about. 'Any' and blank both mean every class.
            grades:
              Array.isArray(row.grades) && row.grades.length
                ? row.grades
                : !row.grade || row.grade === 'Any'
                ? GRADES
                : [row.grade]
          }
        }}
        formToPayload={(v) => ({
          category: v.category,
          teacher: v.teacher,
          apartment: v.apartment,
          date: v.date,
          start_time: v.start_time,
          duration_minutes: Number(v.duration_minutes) || 60,
          // `grade` is derived by the server from this and is read-only there.
          grades: Array.isArray(v.grades) ? v.grades : [],
          kind: v.kind || 'trial',
          price: v.price === '' ? 0 : Number(v.price),
          notes: v.notes || ''
        })}
        refreshKey={`${kind}-${mode}-${refreshKey}`}
        deleteMessage={(row) =>
          `Delete the ${row.categoryName} slot with ${row.teacherName} on ${row.dateLabel}, ${row.timeLabel}? Parents will no longer be able to book it.`
        }
        rowActions={(row) =>
          mode === 'book' && !row.past && row.status === 'open' ? (
            <Button
              size="small"
              disabled={teacherBusy(row)}
              onClick={() => setBooking(row)}
            >
              Book
            </Button>
          ) : null
        }
      />
      <BookTrialDialog
        slot={booking}
        open={Boolean(booking)}
        onClose={() => setBooking(null)}
        onBooked={() => setRefreshKey((k) => k + 1)}
      />
    </>
  )
}

AtHomeSlots.propTypes = {
  kind: PropTypes.oneOf(['trial', 'regular']),
  mode: PropTypes.oneOf(['schedule', 'book'])
}

export default AtHomeSlots
